import {Component, computed, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TextFieldModule} from '@angular/cdk/text-field';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {firstValueFrom, forkJoin, of} from 'rxjs';
import {disabled, form, FormField, required, validate} from '@angular/forms/signals';
import {SharedModule} from '../../../../core/modules/shared.module';
import {EventsService} from '../../../../core/services/events.service';
import {DocumentsService} from '../../../../core/services/documents.service';
import {FuseConfigService} from '../../../../core/services/config.service';
import {TaskDocument, TaskGroup, TaskGroupService} from '../../../../core/services/taskGroup.service';
import {PagesContainerComponent} from '../../pages-container/pages-container.component';
import {CheckModalComponent} from '../../check-save-modal/check-modal';
import {FuseDeleteDialogComponent} from '../../../../core/components/delete-dialog/delete-dialog.component';
import {DocumentComponent} from '../../../../core/components/document/document.component';
import {CertificateTemplateService} from '../../../../core/services/certificateTemplate.service';
import {CertificateSetting, CertificateSettingComponent} from '../../../../core/components/certificate-setting/certificate-setting.component';
import {isEqual} from 'lodash';

interface TaskLocation {
  id: number;
  name: string;
}

interface TaskFile {
  file: File;
  tempId: string;
  access?: string;
}

@Component({
  selector: 'app-task-form',
  templateUrl: './task-form.component.html',
  styleUrls: ['./task-form.component.scss'],
  imports: [SharedModule, TextFieldModule, PagesContainerComponent, DocumentComponent, FormField, CertificateSettingComponent],
  providers: [EventsService, TaskGroupService]
})
export class TaskFormComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private eventService = inject(EventsService);
  private taskService = inject(TaskGroupService);
  private documentsService = inject(DocumentsService);
  private certificateTemplateService = inject(CertificateTemplateService);
  private fuseConfig = inject(FuseConfigService);
  private destroyRef = inject(DestroyRef);
  role = this.fuseConfig.role_Sig;
  certificateEnabled = this.certificateTemplateService.certificateEnabled;
  countries = signal<TaskLocation[]>([]);
  cities = signal<TaskLocation[]>([]);
  organizations = signal<TaskLocation[]>([]);
  taskGroups = signal<TaskGroup[]>([]);
  loading = signal(true);
  loadFailed = signal(false);
  saving = signal(false);
  error = signal('');
  id = this.route.snapshot.queryParamMap.get('id');
  groupId = this.route.snapshot.queryParamMap.get('groupId');
  private saved = false;
  private filesToDelete: TaskDocument[] = [];
  documents = signal<TaskDocument[]>([]);
  getEventDocuments = computed(() => this.documents().map(doc => ({...doc, fileType: doc.name.split('.').pop()})));
  model = signal({
    title: '',
    description: '',
    deadline: null as Date | null,
    taskGroupId: null as number | null,
    countryId: null as number | null,
    cityId: null as number | null,
    organizationId: null as number | null,
    certificateAllowed: false,
    certificateTemplateId: null as number | null,
    files: [] as TaskFile[]
  });
  certificateData = computed<CertificateSetting>(() => ({
    certificateAllowed: this.model().certificateAllowed,
    certificateTemplateId: this.model().certificateTemplateId
  }), {equal: isEqual});
  taskForm = form(this.model, task => {
    required(task.title);
    validate(task.title, ({value}) => value().trim() ? null : {kind: 'required'});
    required(task.taskGroupId);
    required(task.countryId);
    required(task.cityId);
    required(task.organizationId);
    required(task.certificateTemplateId, {
      when: ({valueOf}) => valueOf(task.certificateAllowed)
    });
    disabled(task, () => this.saving());
    disabled(task.countryId, () => this.role() !== 'superAdmin');
    disabled(task.cityId, () => !this.model().countryId || (this.role() !== 'superAdmin' && this.role() !== 'countryAdmin'));
    disabled(task.organizationId, () => !this.model().cityId
      || (this.role() !== 'superAdmin' && this.role() !== 'countryAdmin' && this.role() !== 'subCountryAdmin'));
  });

  ngOnInit() {
    forkJoin({
      groups: this.taskService.getTaskGroupEmpty(),
      task: this.id ? this.taskService.getTaskById(Number(this.id)) : of(null)
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({groups, task}) => {
        this.taskGroups.set(groups);
        if (this.id) {
          if (!task) {
            this.loading.set(false);
            this.loadFailed.set(true);
            this.error.set('tasks.not_found');
            return;
          }
          this.model.set({
            title: task.title,
            description: task.description || '',
            deadline: task.deadline ? new Date(task.deadline) : null,
            taskGroupId: task.taskGroupId,
            countryId: task.countryId || null,
            cityId: task.cityId || null,
            organizationId: task.organizationId || null,
            certificateAllowed: task.certificateAllowed ?? false,
            certificateTemplateId: task.certificateTemplateId ?? null,
            files: []
          });
          this.documents.set(task.documents || []);
        } else {
          if (this.groupId) {
            this.model.update(prev => ({...prev, taskGroupId: Number(this.groupId)}));
          }
        }
        this.loading.set(false);
        this.getCountries();
      },
      error: error => {
        this.loading.set(false);
        this.loadFailed.set(true);
        this.error.set(error.status === 404 ? 'tasks.not_found' : 'tasks.load_error');
      }
    });
  }

  back() {
    this.router.navigate(['/pages/tasks']);
  }

  async canDeactivate(): Promise<boolean> {
    if (this.saving()) {
      return false;
    }
    if (this.saved || !this.taskForm().dirty()) {
      return true;
    }
    const result = await firstValueFrom(this.dialog.open(CheckModalComponent, {
      panelClass: 'unsave-form-dialog',
      width: '400px'
    }).afterClosed());
    if (result === 'leave') {
      return true;
    }
    return result === 'save' ? this.persist() : false;
  }

  private getCountries() {
    this.eventService.getCountry().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (countries: TaskLocation[]) => {
        this.countries.set(countries);
        this.model.update(prev => ({...prev,
          countryId: countries.find(country => country.id === prev.countryId)?.id
            || (countries.length === 1 ? countries[0].id : null)
        }));
        if (this.model().countryId) {
          this.getCities();
        }
      },
      error: () => this.error.set('tasks.load_error')
    });
  }

  countryChanged() {
    this.model.update(prev => ({...prev, cityId: null, organizationId: null}));
    this.cities.set([]);
    this.organizations.set([]);
    this.getCities();
  }

  cityChanged() {
    this.model.update(prev => ({...prev, organizationId: null}));
    this.organizations.set([]);
    this.getOrganizations();
  }

  private getCities() {
    const countryId = this.model().countryId;
    this.eventService.getCity(countryId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (cities: TaskLocation[]) => {
        if (countryId !== this.model().countryId) {
          return;
        }
        this.cities.set(cities);
        this.model.update(prev => ({...prev,
          cityId: cities.find(city => city.id === prev.cityId)?.id
            || (cities.length === 1 ? cities[0].id : null)
        }));
        if (this.model().cityId) {
          this.getOrganizations();
        }
      },
      error: () => this.error.set('tasks.load_error')
    });
  }

  private getOrganizations() {
    const cityId = this.model().cityId;
    this.eventService.getOrganizationByCity(cityId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (organizations: TaskLocation[]) => {
        if (cityId !== this.model().cityId) {
          return;
        }
        this.organizations.set(organizations);
        const organizationId = this.model().organizationId || Number(this.fuseConfig.organizationId_Sig());
        this.model.update(prev => ({...prev,
          organizationId: organizations.find(organization => organization.id === organizationId)?.id
            || (organizations.length === 1 ? organizations[0].id : null)
        }));
      },
      error: () => this.error.set('tasks.load_error')
    });
  }

  onCertificateFormResult(e: CertificateSetting) {
    this.model.update(prev => ({...prev, ...e}));
    this.taskForm.certificateAllowed().markAsDirty();
  }

  addDocuments(files: TaskFile[]) {
    this.model.update(prev => ({...prev, files: files.map(file => ({
      file: file.file,
      tempId: file.tempId,
      access: prev.files.find(item => item.tempId === file.tempId)?.access || 'false'
    }))}));
    this.taskForm.files().markAsDirty();
  }

  handleFileDeleted(file: TaskDocument) {
    this.filesToDelete.push(file);
    this.documents.update(documents => documents.filter(doc => doc.id !== file.id));
    this.taskForm.files().markAsDirty();
  }

  handleNewFileDeleted(tempId: string) {
    this.model.update(prev => ({...prev, files: prev.files.filter(file => file.tempId !== tempId)}));
    this.taskForm.files().markAsDirty();
  }

  async save() {
    if (await this.persist()) {
      this.back();
    }
  }

  async deleteTask() {
    if (!this.id || this.saving() || this.loading() || this.loadFailed()) {
      return;
    }
    this.saving.set(true);
    try {
      const confirmed = await firstValueFrom(this.dialog.open(FuseDeleteDialogComponent, {
        panelClass: 'unsave-form-dialog',
        width: '300px',
        data: {title: 'Delete', message: 'deleteConfirm'}
      }).afterClosed());
      if (!confirmed) {
        return;
      }
      this.error.set('');
      await firstValueFrom(this.taskService.deleteTask(Number(this.id)));
      this.saved = true;
      this.saving.set(false);
      this.back();
    } catch {
      this.error.set('tasks.delete_error');
    } finally {
      this.saving.set(false);
    }
  }

  private async persist(): Promise<boolean> {
    if (this.taskForm().invalid() || !this.model().title.trim() || !this.model().countryId || !this.model().cityId
      || !this.model().organizationId || this.saving() || this.loading() || this.loadFailed()) {
      this.taskForm.title().markAsTouched();
      this.taskForm.taskGroupId().markAsTouched();
      this.taskForm.countryId().markAsTouched();
      this.taskForm.cityId().markAsTouched();
      this.taskForm.organizationId().markAsTouched();
      this.taskForm.deadline().markAsTouched();
      return false;
    }
    this.saving.set(true);
    this.error.set('');
    try {
      const {files, ...task} = this.model();
      const res = await firstValueFrom(this.taskService.addTask({
        ...task,
        ...(this.id ? {id: Number(this.id)} : {}),
        title: task.title.trim(),
        deadline: task.deadline?.toISOString() || null
      }));
      files.forEach(file => {
        const fileExtension = file.file.name.split('.').pop();
        const doc = {
          type: 'task',
          connectedId: res.id,
          access: true,
          files: [{
            file: file.file,
            name: file.file.name,
            type: fileExtension
          }]
        };
        this.documentsService.uploadDocument(doc).subscribe();
      });
      this.filesToDelete.forEach(file => {
        this.documentsService.deleteDocument(file.id).subscribe();
      });
      this.filesToDelete = [];
      this.saved = true;
      return true;
    } catch {
      this.error.set('tasks.save_error');
      return false;
    } finally {
      this.saving.set(false);
    }
  }
}
